import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { testimonials, faqs } from '@/mocks/comics';

export default function About() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫屋',
    url: window.location.origin + (__BASE_PATH__ || ''),
    description: '韩漫屋提供无遮韩国免费漫画大全，支持完整的免费在线阅读服务，资源齐全无删减。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@hanmanwu.com',
      availableLanguage: ['Chinese'],
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: window.location.origin + (__BASE_PATH__ || ''),
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
        item: window.location.origin + (__BASE_PATH__ || '') + '/about',
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫屋 - 无遮韩国免费漫画大全',
    description: '了解韩漫屋品牌故事、用户口碑评价和联系方式。韩漫屋致力于提供最优质的韩国漫画免费在线阅读服务。',
    url: window.location.origin + (__BASE_PATH__ || '') + '/about',
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-3 bg-background-50 border-b border-background-200/70">
          <nav className="max-w-7xl mx-auto flex items-center gap-1.5 text-xs text-foreground-600" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-950">关于我们</span>
          </nav>
        </div>

        {/* Page Header */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              关于韩漫屋
            </h1>
            <p className="mt-3 text-sm text-foreground-600 max-w-lg mx-auto leading-relaxed">
              韩漫屋是无遮韩国免费漫画大全平台，致力于为广大韩漫爱好者提供最优质的在线阅读服务
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/70">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
              <div className="flex-1">
                <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  品牌故事
                </h2>
                <div className="space-y-3 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    韩漫屋成立于2024年，由一群热爱韩国漫画的资深读者共同创建。我们深知每一位韩漫爱好者在寻找高质量漫画资源时的困扰——不完整的章节、混乱的广告、糟糕的阅读体验。正是这些痛点促使我们创建了韩漫屋。
                  </p>
                  <p>
                    我们的<strong>核心使命</strong>是：为中文读者提供<strong>完整、无删减、免费</strong>的韩国漫画在线阅读服务。我们坚信，优质的漫画内容应该被更多人看到，而不应被付费墙所阻挡。
                  </p>
                  <p>
                    经过两年的发展，韩漫屋已经积累了超过<strong>5000部</strong>韩国漫画资源，涵盖恋爱、奇幻、动作、悬疑、搞笑、剧情、古风、都市等丰富分类，月活跃读者突破<strong>100万</strong>。
                  </p>
                  <p>
                    我们的团队每天都在追踪最新韩漫更新动态，确保<strong>第一时间同步</strong>最新章节，让读者不错过任何精彩内容。
                  </p>
                </div>
              </div>
              <div className="w-full lg:w-80 flex-shrink-0">
                <div className="aspect-square rounded-lg overflow-hidden bg-background-200/70">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20cozy%20reading%20corner%20with%20books%20and%20a%20warm%20lamp%2C%20soft%20natural%20lighting%2C%20minimalist%20interior%2C%20peaceful%20atmosphere%2C%20clean%20and%20elegant%20composition%2C%20no%20text%2C%20warm%20cream%20and%20beige%20tones&width=600&height=600&seq=about-brand&orientation=squarish"
                    alt="韩漫屋品牌形象"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 text-center mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              我们的承诺
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {[
                { icon: 'ri-shield-check-line', title: '完整无删减', desc: '所有漫画资源均为完整版本，不含任何删减内容，保证原汁原味的韩漫阅读体验' },
                { icon: 'ri-money-cny-circle-line', title: '完全免费', desc: '无需注册、无需付费，随时随地畅享全部漫画资源，让好内容触手可及' },
                { icon: 'ri-speed-up-line', title: '极速更新', desc: '专业团队每日跟踪最新动态，热门漫画当天同步，让您成为最早的一批读者' },
                { icon: 'ri-device-line', title: '多端适配', desc: '全面适配桌面端和移动端设备，无论在电脑、手机还是平板上都能畅快阅读' },
              ].map((item, index) => (
                <div key={index} className="p-5 rounded-lg bg-background-100/70 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/70">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 text-center mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              读者口碑
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-8">来自韩漫爱好者的真实评价</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="p-5 rounded-lg bg-background-50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-100 flex-shrink-0">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{t.name}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-foreground-600 leading-relaxed">&ldquo;{t.comment}&rdquo;</p>
                  <p className="text-xs text-foreground-500 mt-2">{t.date}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 text-center mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              常见问题
            </h2>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div key={index} className="rounded-lg bg-background-100/70 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 text-left cursor-pointer"
                  >
                    <span className="text-sm font-medium text-foreground-950 pr-4">{faq.question}</span>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${
                      openFaq === index ? 'bg-accent-500 text-background-50' : 'bg-background-200/70 text-foreground-600'
                    }`}>
                      <i className={`ri-${openFaq === index ? 'subtract' : 'add'}-line text-sm`}></i>
                    </div>
                  </button>
                  {openFaq === index && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/70">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
              如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们联系
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-5 rounded-lg bg-background-50 flex flex-col items-center text-center">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-950 mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-600">contact@hanmanwu.com</p>
              </div>
              <div className="p-5 rounded-lg bg-background-50 flex flex-col items-center text-center">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-message-2-line text-lg"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-950 mb-1">意见反馈</h3>
                <p className="text-xs text-foreground-600">欢迎通过邮箱发送您的宝贵意见</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />
    </>
  );
}